import React, { useEffect, useState, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  targetWidth?: number;
  targetHeight?: number;
}

/**
 * A container that scales its content proportionally based on a target resolution.
 * Useful for dashboards designed for a specific screen size (e.g., 2960x1848).
 */
export function ProportionalContainer({ 
  children, 
  targetWidth = 1480, 
  targetHeight = 924 
}: Props) {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const updateScale = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      // Calculate scale factor (contain fit)
      const scaleX = windowWidth / targetWidth;
      const scaleY = windowHeight / targetHeight;
      
      // Use the smaller ratio to ensure content is fully visible (Contain)
      // Alternatively, use scaleX to always fit width
      const newScale = Math.min(scaleX, scaleY);
      
      setScale(newScale);
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [targetWidth, targetHeight]);

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-[#0a0f1a] overflow-hidden"
    >
      <div
        style={{
          width: `${targetWidth}px`,
          height: `${targetHeight}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          flexShrink: 0,
          willChange: 'transform',
          position: 'relative'
        }}
      >
        {children}
      </div>
    </div>
  );
}
